import { DatePipe } from '@angular/common';
import { AfterContentChecked, ChangeDetectorRef, Component, OnInit, ViewContainerRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl, FormArray, ValidatorFn, AbstractControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { AuthService } from '../../../_services/auth.service';
import { DialogService } from '../../../_services/dialog.service';
import { PhotoService } from '../../../_services/photo.service';
import { S3Service } from '../../../_services/s3.service';
import { forkJoin, Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators'
import { DomSanitizer } from '@angular/platform-browser';
import bwipjs from 'bwip-angular2';
import { FaqItem } from '../../../_models/fundraising/FundraisingSite';
import { PledgePerWinSite } from '../../../_models/fundraising/ppw/PledgePerWinSite';
import { PledgePerWinService } from '../../../_services/fundraising/pledgePerWin.service';
import { PledgePerWinSiteService } from '../../../_services/fundraising/pledgePerWinSite.service';
import { UserService } from '../../../_services/user.service';
import { Environment } from '../../../../environments/environment';
import { isEmailValid } from '../../../_shared/utils';
import { VendorService } from '../../../_services/fundraising/vendor.service';

@Component({
  selector: 'app-vendor-website-create',
  templateUrl: './ppw-website-create.component.html',
  styleUrls: ['./ppw-website-create.component.css']
})
export class PledgePerWinWebsiteCreateComponent implements OnInit, AfterContentChecked {

  public form: FormGroup;

  public hasValidationErrors: boolean;

  public ppwSite: PledgePerWinSite;
  public siteId: number;

  public availableHostname: boolean;

  public generatingQR = false;

  public environment: string;

  private defaultFAQs = [
    {
      q: "When will I be charged for my pledge?",
      a: "You can be charged after every game. However, there is a minimum threshold of $30, meaning you won’t see your donations in your bank account until the " +
      "sum of your donations reaches $30 or by the end of the campaign. You will be charged per pledge multiplier. For example, if the pledge multiplier is per goal, " +
      "and you have selected $10 in the checkout process, and if your team scores 2 goals, you will be charged $20."
    },
    {
      q: "How will my donation through pledge per win be used?",
      a: "All gifts received through the pledge per win program will directly benefit the team(s) you have chosen to support. Your support of our teams helps enhance " +
      "the overall experience for the student-athletes on and off the field of competition."
    },
    {
      q: "Can I upgrade or modify my pledge during the season?",
      a: "Yes!! To alter your pledge please click on the My Subscription button at top of our Pledge Per Win website and you will receive an email with instructions on how to cancel your pledge."
    }
  ]

  private legalPdf: File =  null;

  private _hasUpdatePPWSiteName: boolean;
  get hasUpdatePPWSiteName() {
    return this._hasUpdatePPWSiteName;
  }

  private _hasUpdatePPWSite: boolean;
  get hasUpdatePPWSite() {
    return this._hasUpdatePPWSite;
  }

  constructor(
    private fb: FormBuilder,
    private ppwService: PledgePerWinService,
    private ppwSiteService: PledgePerWinSiteService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private spinner: NgxSpinnerService,
    private dialogService: DialogService,
    private viewRef: ViewContainerRef,
    private cdRef: ChangeDetectorRef,
    private datePipe: DatePipe,
    private imageService: PhotoService,
    private s3Service: S3Service,
    private sanitizer: DomSanitizer,
    private userService: UserService,
    private vendorService: VendorService
  ) { }

  ngAfterContentChecked () : void {

    this.cdRef.detectChanges()
  }

  ngOnInit () : void {
    this.environment = Environment.name.includes('dev') || Environment.name.includes('local') ? '.dev.' : '.';

    this.spinner.show();

    const permissionObs = this.userService.hasPermissions(['UpdatePPWSiteName', 'UpdatePPWSite']);
    const siteObs = this.ppwSiteService.getSiteData();

    forkJoin([siteObs, permissionObs])
        .subscribe(
          data => {
            this.spinner.hide();
            this.ppwSite = data[0];

            if (this.ppwSite === null){

              this.siteId = 0;

            } else {

              this.siteId = this.ppwSite.pledgePerWinSiteId;
              this.availableHostname = true;
            }

            const permissions = data[1];

            this._hasUpdatePPWSiteName = permissions['UpdatePPWSiteName'];
            this._hasUpdatePPWSite = permissions['UpdatePPWSite'];

            this.createForm(this.ppwSite);
          },
          error => {

            this.spinner.hide();
            this.dialogService.alert('Unable to load cue5050 site data.', error.error, this.viewRef);
          }
        )
  }

  formControls (name: string) {

    return this.form.controls[name];
  }

  createForm (ppwSite: PledgePerWinSite) {

    this.form = this.fb.group({
      name: [{ value: ppwSite?.name || '', disabled: !this.hasUpdatePPWSiteName }, [Validators.required, Validators.pattern("^(([a-z0-9]|[a-z0-9][a-z0-9\-]*[a-z0-9])\.)*([a-z0-9]|[a-z0-9][a-z0-9\-]*[a-z0-9])$")]],
      primaryColor: [ppwSite?.primaryColor || "#FFFFFF", [Validators.required, Validators.pattern('#([A-Fa-f0-9]{6})')]],
      secondaryColor: [ppwSite?.secondaryColor || "#FFFFFF", [Validators.required, Validators.pattern('#([A-Fa-f0-9]{6})')]],
      legalUrl: [ppwSite?.legalUrl || '', Validators.required],
      bannerUrl: [ppwSite?.bannerUrl || '', Validators.required],
      logoUrl: [ppwSite?.logoUrl || '', Validators.required],
      sponsorUrl: [ppwSite?.sponsorUrl || ''],
      advertisementName: [ppwSite?.advertisementName || ''],
      advertisementText: [ppwSite?.advertisementText || ''],
      advertisementUrl: [ppwSite?.advertisementUrl || ''],
      faq: this.createFaqFormGroup(ppwSite?.faq),
      supportAddresses: [ppwSite?.supportAddresses?.map(e => ({label: e})), [
        ((): ValidatorFn => {
          return (control: AbstractControl): { [key: string]: any } | null => {
            let isValid = true;
            if (control.value !== null) {
              control.value.forEach(em => {
                if (!isEmailValid(em.email || em.label)) {
                  isValid = false;
                }
              });
            }
            return isValid
              ? null
              : { 'email': { value: control.value } };
          };
        })()
        ]
      ],
      baseQRUrl: [ppwSite?.baseQRUrl || ''],
    });

    if (!this.hasUpdatePPWSite) {

      this.form.disable();
    }
  }

  savePdf (file: File) {
    if (file && file.type === 'application/pdf') {

      this.legalPdf = file;
      const tempUrl = URL.createObjectURL(file);
      const safeUrl = this.sanitizer.bypassSecurityTrustUrl(tempUrl);
      this.form.controls.legalUrl.patchValue(safeUrl);

    } else {

      this.form.controls.legalUrl.setErrors({ notPdf: true });
      this.form.controls.legalUrl.markAsTouched();
    }
  }

  uploadPdf (file: File): Observable<string> {

    if (file && file.type === 'application/pdf') {

      const lastDotIdx = file.name.lastIndexOf('.');
      const name = file.name.substring(0, lastDotIdx);
      const extension = file.name.substring(lastDotIdx + 1);
      const uniqueName = `${name}_${Date.now()}.${extension}`;
      return this.s3Service.uploadFile(file, `fundraising/${localStorage.getItem('vendorName')}/ppw/legal/` + uniqueName, "get-signed-s3-url",'application/pdf')
        .pipe(map(
          uploadUrl => {
            return Environment.s3CdnUrl + this.s3Service.getUrlWithoutSignature(uploadUrl.split('s3.amazonaws.com')[1])
          }
        ))

    } else {

        this.form.controls.logoUrl.setErrors({ notPdf: true });
        this.form.controls.logoUrl.markAsTouched();
    }
  }

  deletePdf () {

    if (this.formControls('legalUrl').value !== '') {

      this.dialogService
      .confirm(
        'This will remove and delete your PDF.',
        'Are you sure you want to do this?',
        this.viewRef)
        .then(
          result => {

          if (result) {

            if (this.siteId > 0) {

              const lastSlashIdx = this.formControls('legalUrl').value.lastIndexOf('/');
              const name = this.formControls('legalUrl').value.substring(lastSlashIdx + 1);
              this.ppwSiteService.deleteLegalPdf(name)
                .subscribe(
                  () => {

                    this.form.patchValue({ legalUrl: ''});
                    const siteData = new PledgePerWinSite(this.form.getRawValue());

                    this.ppwSiteService.update(siteData)
                      .subscribe(
                        res => {

                          this.dialogService.alert(
                            "File Deleted.",
                            "Your legal PDF has been deleted.",
                            this.viewRef);
                        },
                        error => {

                          this.spinner.hide();
                          this.dialogService.alert('Oops something went wrong..', error.error, this.viewRef);
                        }
                      );
                },
                error => {

                  this.spinner.hide();
                  this.dialogService.alert('Oops something went wrong..', error.error, this.viewRef);
                });

              } else {

                this.form.patchValue({ legalUrl: ''});
              }
            }
          }
        );
    }
  }

  uploadImage (file: File, type: string, correctDimensions: boolean) {

    switch (type) {

      case 'logo':

        if (file && file.type === 'image/png' && correctDimensions) {

          this.imageService.enqueueUploadPhoto(file, 'logo-image', { height: 44, width: 60 }, false, true)
            .subscribe(
              res => {

                this.form.patchValue({ logoUrl: res.url });
              }
            );

        } else {

          this.form.controls.logoUrl.setErrors({ incorrectDimensions: true});
          this.form.controls.logoUrl.markAsTouched();
        }
        break;

      case 'banner':

        if (file && file.type.includes('image') && correctDimensions) {

          this.imageService.enqueueUploadPhoto(file, 'banner-image', { height: 768, width: 1366 }, false, true)
            .subscribe(
              res => {

                this.form.patchValue({ bannerUrl: res.url });
              }
            );

        } else {

          this.form.controls.bannerUrl.setErrors({ incorrectDimensions: true });
          this.form.controls.bannerUrl.markAsTouched();
        }
        break;

      case 'sponsor':

        if (file && file.type.includes('image') && correctDimensions) {

          this.imageService.enqueueUploadPhoto(file, 'sponsor-image', { height: 318, width: 1080 }, false, true)
            .subscribe(
              res => {

                this.form.patchValue({ sponsorUrl: res.url });
              }
            );

        } else {

          this.form.controls.sponsorUrl.setErrors({ incorrectDimensions: true});
          this.form.controls.sponsorUrl.markAsTouched();
        }

      default:
        break;
    }

    this.form.markAsDirty();
  }

  verifyImageDimensions (file: File, type: string) {

    const verifyAspectRatio = type === 'banner' ? true : false;
    let correctWidth = type === 'banner' ? 1366 : type === 'logo' ? 60 : type === 'sponsor' ? 1080 : 0;
    let correctHeight = type === 'banner' ? 768 : type === 'logo' ? 44 : type === 'sponsor' ? 318 : 0;

    let dimensionObs = of(false);

    if (verifyAspectRatio) {

      dimensionObs = this.imageService.meetsImageConstraints(file, correctWidth, correctHeight, 1.77, 0.01);

    } else {

      dimensionObs = this.imageService.meetsImageConstraints(file, correctWidth, correctHeight);
    }

    dimensionObs
      .subscribe(
        correct => {

          this.uploadImage(file, type, correct);
        }
      );
  }

  validateUrl () {

    const regex = new RegExp(/https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/gm);

    const controlValue = this.form.controls.advertisementUrl.value;

    if (!regex.test(controlValue) && controlValue.length > 0) {

      this.form.controls.advertisementUrl.setErrors({invalid: true})
      this.form.controls.advertisementUrl.markAsTouched();
    }
  }

  checkHostnameAvailability () {

    this.ppwSiteService.getHostnameAvailability(this.formControls('name').value)
      .subscribe(
        res => {

          this.availableHostname = res;
        }
      );
  }

  createFaqFormGroup (faqItems: FaqItem[]) {

    const f = new FormArray<FormGroup>([]);

    if (faqItems && faqItems.length > 0) {

      faqItems.forEach(q =>
        f.push(this.fb.group({
          head: [q.head, Validators.required],
          paragraph: [q.paragraph, Validators.required],
        }))
      );

    } else {

      for (let i = 0; i < 3; i++) {

        f.push(this.fb.group({
          head: [this.defaultFAQs[i].q, Validators.required],
          paragraph: [this.defaultFAQs[i].a, Validators.required],
        }));
      }
    }

    return f;
  }

  newFaqItem () {

    (<FormArray>this.form.controls.faq)
      .push(
        this.fb.group({
          head: [null, Validators.required],
          paragraph: [null, Validators.required],
        })
      );
    this.form.controls.faq.markAsDirty();
    this.form.updateValueAndValidity();
  }

  deleteFaqItem (tierIndex: number) {

    (<FormArray>this.form.controls.faq).removeAt(tierIndex);
    this.form.controls.faq.markAsDirty();
    this.form.updateValueAndValidity();
  }

  public onNavigatingAway () {

    if (!this.form || !this.form.dirty) {

      return true;

    } else {

      return this.dialogService.confirm('Unsaved Changes', 'Are you sure you want to continue? Any unsaved changes will be lost!', this.viewRef);
    }
  }

  generateBaseQRCode () {

    this.generatingQR = true;
    this.spinner.show('qrSpinner');

    this.generateQRCodeFile('Base', `https://${this.form.get('name').value + this.environment}pledgeperwin.com`)
      .subscribe(
        file => {

          this.spinner.hide('qrSpinner');

          this.imageService.enqueueUploadPhoto(file, file.name, { height: 2400, width: 2400 }, false, true)
              .subscribe(
                res => {

                  this.generatingQR = false;
                  this.form.markAsDirty();
                  this.form.patchValue({baseQRUrl: res.url});
                }
              )
        }
      )
  }


  generateQRCodeFile (name: string, link: string) : Observable<File> {

    const canvas = document.createElement('canvas');

    return new Observable<File>(subscriber => {

      bwipjs(canvas, {
        bcid: 'qrcode',
        text: link,
        scale: 3,               // 3x scaling factor
        height: 635,
        width: 635,              // Bar height, in millimeters
        includetext: true,            // Show human-readable text only if not code 128
        textxalign: 'center',        // Always good to set this
      }, async (err, cvs) => {

        if (err) {

          console.log(err);

        } else {

          canvas.toBlob(
            (blob) => {

              const file = new File([blob], `QR-${name}.png`, {type:'image/png'});
              subscriber.next(file);
              subscriber.complete();
            },'image/png', 1)
        }
      });
    })
  }

  async save () {

    this.hasValidationErrors = !this.form.valid;
    const invalid = [];
    const controls = this.form.controls;

    for (const name in controls) {

        if (controls[name].invalid) {

            invalid.push(name);
        }
    }

    if (this.hasValidationErrors) {

      this.form.markAllAsTouched();
      this.form.markAsDirty();
      return;
    }

    this.spinner.show();

    const imageObs = this.imageService.commitUploads();
    const pdfObs = this.legalPdf ? this.uploadPdf(this.legalPdf) : of(null);
    const supportAddressesObs = this.vendorService.addVendorSupportEmails(this.form.controls.supportAddresses.value?.map(v => v.label) ?? []);

    forkJoin([imageObs, pdfObs, supportAddressesObs])
      .pipe(mergeMap(
        (data) => {

          if (data[1]) {

            this.form.patchValue({ legalUrl: data[1] });
          }

          let formData = this.form.getRawValue();

          delete formData.supportAddresses;

          const siteData = new PledgePerWinSite(formData);
          siteData.name = siteData.name.toLowerCase();

          if (this.siteId > 0) {

            return this.ppwSiteService.update(siteData);

          } else {

            return this.ppwSiteService.create(siteData);
          }
        }
      ))
    .subscribe(
      newSite => {

        this.spinner.hide();
        this.form.markAsPristine();

        this.dialogService
          .confirm(
            'Website Saved!',
            'Would you like to continue editing?',
            this.viewRef)
          .then(
            result => {

            if (!result) {

             this.handleCancel();
           }
          }
        );
      },
      err => {

        this.spinner.hide();
        this.dialogService.alert('Oops something went wrong..', 'We were unable to process your request at this time, please try again in a few moments. Please contact CUE support at support@connectwithcue.com if you continue to experience issues', this.viewRef);
      });
  }

  public handleCancel () {
    this.ngOnInit()
  }

}
